import type {
  AdminOrderRow,
  LogisticsQueueOrder,
  OrderSummary,
} from '../api/orders';
import type { ExternalOrderRow } from '../api/externalOrders';
import type { SelectedPaczkomat } from '../util/paczkomat';
import type { DashboardData, DashboardPeriod, DashboardPoint, ShirtCut, Size } from '../types';

/**
 * Fixtures for demo mode. Everything here is invented.
 *
 * Conventions that keep demo data distinguishable from production at a glance:
 *   - external order ids are DEMO-0001…, shop order ids DEMO-26-… (real ones are OPT-26-…)
 *   - every address is a real street in a real city but an invented number
 *   - every email is @example.invalid, a reserved TLD that can never receive mail (RFC 2606)
 *
 * Holder names deliberately include Polish diacritics so a presenter can show that holder
 * matching folds them.
 */

const CONCERT_DATE = '2026-11-28';
const PRICE = 12_900;

function iso(daysAgo: number, hour = 12, minute = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - daysAgo);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ---------------------------------------------------------------- external orders

const HOLDERS: Array<[string, string]> = [
  ['Łukasz', 'Bednarek'],
  ['Michał', 'Wróbel'],
  ['Agnieszka', 'Śliwińska'],
  ['Zofia', 'Kaczmarek'],
  ['Rafał', 'Głowacki'],
  ['Katarzyna', 'Zielińska'],
  ['Tomasz', 'Mazur'],
  ['Weronika', 'Dąbrowska'],
  ['Piotr', 'Jankowski'],
  ['Anna', 'Lewandowska'],
  ['Krzysztof', 'Sikora'],
  ['Magdalena', 'Wysocka'],
  ['Bartosz', 'Ostrowski'],
  ['Natalia', 'Górska'],
  ['Jakub', 'Pawlak'],
  ['Ewa', 'Sobczak'],
  ['Grzegorz', 'Witkowski'],
  ['Alicja', 'Baranowska'],
  ['Marcin', 'Kubiak'],
  ['Paulina', 'Czerwińska'],
  ['Adrian', 'Malinowski'],
  ['Joanna', 'Szymczak'],
  ['Damian', 'Krawczyk'],
  ['Oliwia', 'Adamczyk'],
];

const DEMO_SHIRT_QUOTA_PER_COLOR = 1;

// Every third row buys two tickets, so a presenter can show both the single-ticket allowance
// and one that survives a partial order.
const DEMO_EXTERNAL_ORDERS: ExternalOrderRow[] = HOLDERS.map(([firstName], i) => {
  const ticketCount = i % 3 === 0 ? 2 : 1;
  const perColor = ticketCount * DEMO_SHIRT_QUOTA_PER_COLOR;
  // A deterministic but uneven pattern, so the figures look organic.
  const black = i % 3 === 2 ? 0 : Math.min(perColor, (i % 4) + 1);
  const white = i % 3 === 2 ? 0 : Math.min(perColor, i % 2);
  const orderedShirts = black + white;
  const allowance = perColor * 2;
  return {
    externalId: `DEMO-${String(i + 1).padStart(4, '0')}`,
    firstName,
    ticketCount,
    orderedShirts,
    allowance,
    remaining: allowance - orderedShirts,
    byColor: {
      czarna: { allowance: perColor, alreadyOrdered: black, remaining: perColor - black },
      biała: { allowance: perColor, alreadyOrdered: white, remaining: perColor - white },
    },
    used: orderedShirts > 0,
  };
});

// ---------------------------------------------------------------- orders

type OrderSeed = {
  idSuffix: string;
  holderIndex: number;
  status: OrderSummary['status'];
  method: 'paczkomat' | 'kurier' | 'odbior' | 'zagranica';
  items: Array<[ShirtCut, Size, number]>;
  daysAgo: number;
  isManual?: boolean;
  tracking?: string;
  comment?: [string, string];
  mergedIntoId?: string;
  packageSize?: string;
};

const PACZKOMATY: SelectedPaczkomat[] = [
  { code: 'KRA01M', name: 'KRA01M', street: 'Długa 12', postCode: '31-147', city: 'Kraków', latitude: 50.0685, longitude: 19.9401 },
  { code: 'WAW47A', name: 'WAW47A', street: 'Marszałkowska 88', postCode: '00-514', city: 'Warszawa', latitude: 52.2264, longitude: 21.0122 },
  { code: 'GDA12B', name: 'GDA12B', street: 'Grunwaldzka 204', postCode: '80-266', city: 'Gdańsk', latitude: 54.3838, longitude: 18.5994 },
  { code: 'POZ09C', name: 'POZ09C', street: 'Półwiejska 42', postCode: '61-888', city: 'Poznań', latitude: 52.4003, longitude: 16.9209 },
  { code: 'WRO22D', name: 'WRO22D', street: 'Legnicka 58', postCode: '54-204', city: 'Wrocław', latitude: 51.1188, longitude: 16.9788 },
];

const COURIERS: Array<[string, string, string]> = [
  ['Kwiatowa 17/3', '30-105', 'Kraków'],
  ['Słoneczna 4', '02-495', 'Warszawa'],
  ['Morska 61/12', '81-233', 'Gdynia'],
];

const ORDER_SEEDS: OrderSeed[] = [
  { idSuffix: 'A7K2', holderIndex: 0, status: 'paid', method: 'paczkomat', items: [['męski', 'L', 2]], daysAgo: 1 },
  { idSuffix: 'B3M9', holderIndex: 1, status: 'paid', method: 'kurier', items: [['damski', 'M', 1], ['męski', 'XL', 1]], daysAgo: 1,
    comment: ['combine', 'Poproszę razem z zamówieniem koleżanki, jedziemy razem.'] },
  { idSuffix: 'C8P4', holderIndex: 2, status: 'shipped', method: 'paczkomat', items: [['damski', 'S', 1]], daysAgo: 4, tracking: '629900112233445566', packageSize: 'A' },
  { idSuffix: 'D2R7', holderIndex: 3, status: 'awaiting_payment', method: 'paczkomat', items: [['damski', 'M', 3]], daysAgo: 0 },
  { idSuffix: 'E5T1', holderIndex: 4, status: 'paid', method: 'odbior', items: [['męski', 'XXL', 1]], daysAgo: 2 },
  { idSuffix: 'F9W6', holderIndex: 5, status: 'shipped', method: 'kurier', items: [['damski', 'L', 2]], daysAgo: 6, tracking: '629900998877665544', packageSize: 'B' },
  { idSuffix: 'G4X3', holderIndex: 6, status: 'paid', method: 'paczkomat', items: [['męski', 'M', 1]], daysAgo: 3 },
  { idSuffix: 'H7Y8', holderIndex: 7, status: 'cancelled', method: 'paczkomat', items: [['damski', 'XS', 1]], daysAgo: 8 },
  { idSuffix: 'J1Z5', holderIndex: 8, status: 'paid', method: 'kurier', items: [['męski', 'L', 1], ['damski', 'S', 1]], daysAgo: 2 },
  { idSuffix: 'K6A2', holderIndex: 9, status: 'pickup', method: 'odbior', items: [['damski', 'M', 1]], daysAgo: 5, isManual: true },
  { idSuffix: 'L3B9', holderIndex: 10, status: 'paid', method: 'paczkomat', items: [['męski', '3XL', 1]], daysAgo: 1 },
  { idSuffix: 'M8C4', holderIndex: 11, status: 'merged', method: 'paczkomat', items: [['damski', 'L', 1]], daysAgo: 3, mergedIntoId: 'DEMO-26-B3M9' },
  { idSuffix: 'N2D7', holderIndex: 12, status: 'paid', method: 'kurier', items: [['męski', 'XL', 2]], daysAgo: 4,
    comment: ['other', 'Czy koszulki są z bawełny organicznej?'] },
  { idSuffix: 'P5E1', holderIndex: 13, status: 'shipped', method: 'paczkomat', items: [['damski', 'M', 1]], daysAgo: 9, tracking: '629900556677889900', packageSize: 'A' },
  { idSuffix: 'Q9F6', holderIndex: 14, status: 'paid', method: 'paczkomat', items: [['męski', 'S', 1]], daysAgo: 0 },
  { idSuffix: 'R4G3', holderIndex: 15, status: 'awaiting_payment', method: 'odbior', items: [['damski', 'XL', 1]], daysAgo: 0 },
  { idSuffix: 'S7H8', holderIndex: 16, status: 'paid', method: 'paczkomat', items: [['męski', 'M', 2]], daysAgo: 5 },
  { idSuffix: 'T1J5', holderIndex: 17, status: 'paid', method: 'kurier', items: [['damski', 'S', 1]], daysAgo: 7 },
  { idSuffix: 'U6K4', holderIndex: 18, status: 'paid', method: 'zagranica', items: [['męski', 'L', 1]], daysAgo: 2,
    comment: ['other', 'Jestem na Teneryfie do marca, paczka może iść pocztą.'] },
  { idSuffix: 'V3L2', holderIndex: 19, status: 'paid', method: 'odbior', items: [['damski', 'M', 1], ['męski', 'XL', 1]], daysAgo: 3 },
  { idSuffix: 'W8M5', holderIndex: 21, status: 'paid', method: 'zagranica', items: [['męski', 'M', 2]], daysAgo: 6,
    tracking: 'RR123456785ES' },
];

function buildOrder(seed: OrderSeed): AdminOrderRow {
  const [firstName, lastName] = HOLDERS[seed.holderIndex]!;
  const itemsTotal = seed.items.reduce((sum, [, , qty]) => sum + qty * PRICE, 0);
  // Mirrors parcelForOrder(): neither pickup nor a foreign parcel is priced at checkout.
  const shippingCost =
    seed.method === 'odbior' || seed.method === 'zagranica'
      ? 0
      : seed.method === 'kurier'
        ? 1500
        : 1290;
  const paczkomat = PACZKOMATY[seed.holderIndex % PACZKOMATY.length]!;
  const courier = COURIERS[seed.holderIndex % COURIERS.length]!;

  return {
    id: `DEMO-26-${seed.idSuffix}`,
    status: seed.status,
    customerName: `${firstName} ${lastName}`,
    email: `${firstName.toLowerCase()}.${lastName.toLowerCase()}@example.invalid`,
    phone: `+48 ${500 + seed.holderIndex} ${100 + seed.holderIndex} ${200 + seed.holderIndex}`,
    externalOrderId: seed.isManual ? null : `DEMO-${String(seed.holderIndex + 1).padStart(4, '0')}`,
    shippingMethod: seed.method,
    shippingAddress:
      seed.method === 'paczkomat'
        ? { paczkomat }
        : seed.method === 'kurier'
          ? { courier: { street: courier[0], postalCode: courier[1], city: courier[2] } }
          : seed.method === 'zagranica'
            ? { international: { country: 'ES', countryName: 'Hiszpania' } }
            : { pickupNote: 'Odbiór przy stanowisku merch przed koncertem' },
    shippingCostGrosze: shippingCost,
    itemsTotalGrosze: itemsTotal,
    totalGrosze: itemsTotal + shippingCost,
    isManual: seed.isManual ?? false,
    mergedIntoId: seed.mergedIntoId ?? null,
    shipsWithId: null,
    packageSize: seed.packageSize ?? null,
    trackingNumber: seed.tracking ?? null,
    paymentProvider: seed.isManual ? 'cash' : seed.status === 'awaiting_payment' ? null : 'stripe',
    customerCommentTopic: seed.comment?.[0] ?? null,
    customerCommentText: seed.comment?.[1] ?? null,
    createdAt: iso(seed.daysAgo, 9 + (seed.holderIndex % 8), (seed.holderIndex * 7) % 60),
    paidAt: seed.status === 'awaiting_payment' ? null : iso(seed.daysAgo, 10 + (seed.holderIndex % 8)),
    shippedAt: seed.status === 'shipped' ? iso(Math.max(0, seed.daysAgo - 2), 14) : null,
    cancelledAt: seed.status === 'cancelled' ? iso(Math.max(0, seed.daysAgo - 1), 16) : null,
    internalNote: seed.isManual ? 'Zamówienie dodane ręcznie — płatność gotówką na próbie.' : null,
    items: seed.items.map(([cut, size, qty], i) => ({
      id: seed.holderIndex * 10 + i + 1,
      productId: `demo-${cut === 'męski' ? 'm' : 'd'}-${size.toLowerCase()}`,
      cut,
      size,
      qty,
      unitPriceGrosze: PRICE,
    })),
  };
}

const DEMO_ORDERS: AdminOrderRow[] = ORDER_SEEDS.map(buildOrder);

// ---------------------------------------------------------------- dashboard

function seriesFor(period: DashboardPeriod): { unit: 'hour' | 'day'; points: DashboardPoint[] } {
  if (period === 'day') {
    const shape = [0, 0, 0, 0, 0, 0, 1, 1, 2, 3, 2, 4, 5, 3, 4, 6, 5, 7, 6, 4, 3, 2, 1, 0];
    return {
      unit: 'hour',
      points: shape.map((count, h) => ({ at: `${String(h).padStart(2, '0')}:00`, count })),
    };
  }
  const days = period === 'week' ? 7 : 30;
  const shape = [3, 5, 4, 8, 6, 11, 9, 7, 12, 10, 6, 8, 14, 11, 9, 13, 7, 10, 12, 8, 15, 11, 9, 14, 10, 12, 16, 13, 11, 18];
  const slice = shape.slice(-days);
  return {
    unit: 'day',
    points: slice.map((count, i) => ({ at: iso(days - 1 - i, 0).slice(0, 10), count })),
  };
}

const PERIOD_TOTALS: Record<DashboardPeriod, DashboardData['totals']> = {
  day: { orders: 12, ordersDelta: 3, shirts: 17, shirtsDelta: 4, toShip: 9, toShipOver48h: 1, cancelled: 1, cancelledRate: 8.3 },
  week: { orders: 64, ordersDelta: 11, shirts: 91, shirtsDelta: 18, toShip: 23, toShipOver48h: 3, cancelled: 4, cancelledRate: 6.3 },
  month: { orders: 248, ordersDelta: 37, shirts: 352, shirtsDelta: 61, toShip: 41, toShipOver48h: 5, cancelled: 13, cancelledRate: 5.2 },
};

const PERIOD_HEADING: Record<DashboardPeriod, string> = {
  day: 'Dzisiaj',
  week: 'Ostatnie 7 dni',
  month: 'Ostatnie 30 dni',
};

function dashboard(period: DashboardPeriod): DashboardData {
  const { unit, points } = seriesFor(period);
  const totals = PERIOD_TOTALS[period];
  const shirts = totals.shirts;
  const sizeShape: Array<[Size, number]> = [
    ['XS', 3], ['S', 12], ['M', 26], ['L', 24], ['XL', 18], ['XXL', 11], ['3XL', 6],
  ];
  const sizeSum = sizeShape.reduce((s, [, pct]) => s + pct, 0);
  const daysToConcert = Math.max(
    0,
    Math.ceil((new Date(`${CONCERT_DATE}T20:00:00Z`).getTime() - Date.now()) / 86_400_000),
  );

  return {
    totals,
    period,
    periodHeading: PERIOD_HEADING[period],
    seriesUnit: unit,
    series: points,
    concertDate: CONCERT_DATE,
    daysToConcert,
    countdown: `${daysToConcert} dni do koncertu`,
    sizes: sizeShape.map(([size, pct]) => ({
      size,
      count: Math.round((shirts * pct) / sizeSum),
      pct: Math.round((pct / sizeSum) * 1000) / 10,
    })),
    colorCut: [
      { name: 'Czarna · męski', count: Math.round(shirts * 0.42), pct: 42 },
      { name: 'Czarna · damski', count: Math.round(shirts * 0.31), pct: 31 },
      { name: 'Biała · męski', count: Math.round(shirts * 0.16), pct: 16 },
      { name: 'Biała · damski', count: Math.round(shirts * 0.11), pct: 11 },
    ],
    shipping: {
      paczkomat: { count: Math.round(totals.orders * 0.6), pct: 60 },
      kurier: { count: Math.round(totals.orders * 0.25), pct: 25 },
      odbior: { count: Math.round(totals.orders * 0.11), pct: 11 },
      zagranica: { count: Math.round(totals.orders * 0.04), pct: 4 },
    },
  };
}

// ---------------------------------------------------------------- logistics

function logisticsQueue(): LogisticsQueueOrder[] {
  // Same filter as GET /logistics/queue: every paid parcel, except a foreign one whose carrier
  // number has not been entered yet.
  return DEMO_ORDERS.filter(
    (o) => o.status === 'paid' && (o.shippingMethod !== 'zagranica' || o.trackingNumber !== null),
  ).map((o) => ({
    id: o.id,
    customerName: o.customerName,
    phone: o.phone,
    externalOrderId: o.externalOrderId,
    shippingMethod: o.shippingMethod,
    shippingAddress: o.shippingAddress,
    trackingNumber: o.trackingNumber,
    packageSize: o.packageSize,
    customerCommentTopic: o.customerCommentTopic,
    customerCommentText: o.customerCommentText,
    isManual: o.isManual,
    mergedIntoId: o.mergedIntoId,
    unpaidRideAlongs: [],
    createdAt: o.createdAt,
    items: o.items.map((it) => ({
      productId: it.productId,
      color: it.productId.includes('-m-') ? 'czarna' : 'biała',
      cut: it.cut,
      size: it.size,
      qty: it.qty,
      fromOrderId: null,
    })),
  }));
}

// ---------------------------------------------------------------- dispatch

/**
 * Maps a GET path to fixture data, or returns undefined when demo mode should not answer and the
 * request may go to the network. Query parameters are honoured so search and the period selector
 * behave like the real thing.
 */
export function getDemoResponse(path: string): unknown | undefined {
  const [rawPath = '', rawQuery = ''] = path.split('?');
  const qs = new URLSearchParams(rawQuery);

  if (rawPath === '/admin/dashboard') {
    const p = qs.get('period');
    const period: DashboardPeriod = p === 'day' || p === 'month' ? p : 'week';
    return dashboard(period);
  }

  if (rawPath === '/admin/counts') {
    return {
      orders: DEMO_ORDERS.filter((o) => o.status === 'awaiting_payment').length,
      logistics: logisticsQueue().length,
    };
  }

  if (rawPath === '/admin/external-orders') {
    const q = (qs.get('q') ?? '').trim().toLowerCase();
    const matched = q
      ? DEMO_EXTERNAL_ORDERS.filter(
          (t) => t.externalId.toLowerCase().includes(q) || t.firstName.toLowerCase().includes(q),
        )
      : DEMO_EXTERNAL_ORDERS;
    return {
      externalOrders: matched,
      total: DEMO_EXTERNAL_ORDERS.length,
      used: DEMO_EXTERNAL_ORDERS.filter((t) => t.used).length,
      shirtQuotaPerColor: DEMO_SHIRT_QUOTA_PER_COLOR,
    };
  }

  if (rawPath === '/admin/logistics/queue') {
    const queue = logisticsQueue();
    return { queue, cursor: null, total: queue.length, sentToday: 4 };
  }

  if (rawPath === '/admin/orders') {
    const status = qs.get('status');
    const q = (qs.get('q') ?? '').trim().toLowerCase();
    let rows = DEMO_ORDERS;
    if (status) rows = rows.filter((o) => o.status === status);
    if (q) {
      rows = rows.filter(
        (o) =>
          o.id.toLowerCase().includes(q) ||
          o.customerName.toLowerCase().includes(q) ||
          (o.externalOrderId ?? '').toLowerCase().includes(q),
      );
    }
    return { orders: rows, total: rows.length };
  }

  if (rawPath === '/admin/shop-config') {
    return {
      config: {
        id: 'current',
        shopOpen: true,
        closureMessage: '',
        estimatedShipDate: 'do 14 dni od zamknięcia zbiórki (DEMO)',
        paczkomatCostAGrosze: 1649,
        paczkomatCostBGrosze: 1849,
        paczkomatCostCGrosze: 2049,
        kurierCostAGrosze: 1949,
        kurierCostBGrosze: 2049,
        kurierCostCGrosze: 2549,
        ticketShirtQuotaPerColor: DEMO_SHIRT_QUOTA_PER_COLOR,
        boothAddress: 'Stoisko przy wejściu B (DEMO)',
        courierDropoffPoint: 'KRA01M',
        concertDate: CONCERT_DATE,
        updatedAt: iso(3),
      },
    };
  }

  if (rawPath === '/admin/products') {
    const sizes = (productId: string, cut: ShirtCut) =>
      (['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'] as Size[]).map((size, i) => ({
        id: `${productId}-${cut === 'męski' ? 'm' : 'd'}-${size}`,
        productId,
        cut,
        size,
        displayOrder: i,
        createdAt: iso(60),
        deletedAt: null,
      }));
    return {
      products: [
        {
          id: 'demo-czarna',
          color: 'czarna',
          name: 'Koszulka fanowska — czarna (DEMO)',
          description: 'Nadruk sitodrukowy, przod + rekaw.',
          priceGrosze: PRICE,
          material: '100% bawełna organiczna, 180 g/m²',
          printing: 'Sitodruk, 3 kolory',
          images: [],
          imageUrl: null,
          displayOrder: 0,
          createdAt: iso(60),
          deletedAt: null,
          sizes: [...sizes('demo-czarna', 'męski'), ...sizes('demo-czarna', 'damski')],
        },
        {
          id: 'demo-biala',
          color: 'biała',
          name: 'Koszulka fanowska — biała (DEMO)',
          description: 'Nadruk sitodrukowy, przód.',
          priceGrosze: PRICE,
          material: '100% bawełna organiczna, 180 g/m²',
          printing: 'Sitodruk, 2 kolory',
          images: [],
          imageUrl: null,
          displayOrder: 1,
          createdAt: iso(60),
          deletedAt: null,
          sizes: [...sizes('demo-biala', 'męski'), ...sizes('demo-biala', 'damski')],
        },
      ],
    };
  }

  const activity = /^\/admin\/orders\/([^/]+)\/activity$/.exec(rawPath);
  if (activity) {
    const order = DEMO_ORDERS.find((o) => o.id === activity[1]!);
    if (!order) return { activity: [] };
    return {
      activity: [
        { at: order.createdAt, kind: 'order.created', actor: null, meta: {} },
        ...(order.paidAt
          ? [{ at: order.paidAt, kind: 'order.paid', actor: null, meta: { provider: order.paymentProvider } }]
          : []),
        ...(order.shippedAt
          ? [{ at: order.shippedAt, kind: 'order.shipped', actor: 'demo', meta: { tracking: order.trackingNumber } }]
          : []),
      ],
    };
  }

  const detail = /^\/admin\/orders\/([^/]+)$/.exec(rawPath);
  if (detail) {
    const order = DEMO_ORDERS.find((o) => o.id === detail[1]!);
    if (order) return { order, items: order.items };
  }

  return undefined;
}
